import { useCallback, useEffect, useState } from "react";
import { PlayerAIProblem, type PlayerAIClient } from "./client.js";
import type { BackgroundCanvasStatus } from "./types.js";

const STAGE_LABELS: Partial<
  Record<NonNullable<BackgroundCanvasStatus["stage"]>, string>
> = {
  requested: "Canvas review requested",
  queued: "Canvas review queued…",
  reviewing_canvas: "Reviewing canvas…",
  reading_widget: "Reading canvas…",
  preparing_widget_change: "Preparing canvas changes…",
  preparing_canvas_proposal: "Preparing canvas proposal…",
  awaiting_approval: "Canvas changes need approval",
  applying: "Updating canvas…",
  awaiting_canvas_delivery: "Waiting for canvas delivery…",
  updated: "Canvas Updated",
  no_change: "Canvas reviewed — no changes needed",
  needs_input: "Canvas review needs more information",
  conflict: "Canvas review found a conflict",
  failed: "Canvas update failed",
  rejected: "Canvas proposal rejected",
  expired: "Canvas proposal expired",
};

export function canvasStatusText(
  status: BackgroundCanvasStatus,
  now: number,
): string | null {
  const stage =
    status.stage ??
    (status.state === "analysing" ? "reviewing_canvas" : status.state);
  if (stage === "idle") return null;
  if (["updated", "rejected"].includes(stage)) {
    const elapsed = status.completed_at
      ? now - Date.parse(status.completed_at)
      : Infinity;
    if (elapsed < 0 || elapsed >= 5000) return null;
  }
  if (stage === "needs_input")
    return status.missing_input
      ? `Canvas unchanged. More information needed: ${status.missing_input}`
      : `Canvas unchanged. ${status.summary ?? "More information is needed to complete this update."}`;
  if (stage === "no_change")
    return `Canvas unchanged. ${status.summary ?? "The review produced no changes."}`;
  if (stage === "conflict")
    return `Canvas unchanged. ${status.conflict ?? status.summary ?? "Conflicting information needs to be resolved before updating it."}`;
  const widget = status.widget_labels?.at(-1);
  let label = STAGE_LABELS[stage] ?? null;
  if (widget && (stage !== "updated" || status.widget_labels?.length === 1)) {
    const named: Partial<
      Record<NonNullable<BackgroundCanvasStatus["stage"]>, string>
    > = {
      reading_widget: `Reading ${widget}…`,
      preparing_widget_change: `Preparing ${widget} changes…`,
      applying: `Updating ${widget}…`,
      updated: `${widget} updated`,
    };
    label = named[stage] ?? label;
  }
  if (
    stage === "updated" &&
    status.total_updates != null &&
    status.applied_updates != null
  ) {
    label = `${label} · ${status.applied_updates} of ${status.total_updates} updates applied`;
  }
  if (status.state === "analysing" && status.started_at && label) {
    const seconds = Math.max(
      0,
      Math.floor((now - Date.parse(status.started_at)) / 1000),
    );
    if (Number.isFinite(seconds))
      label += ` · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  }
  return label;
}

export function useBackgroundCanvasWork(
  client: Pick<PlayerAIClient, "backgroundCanvasStatus">,
  conversationId: string | null,
) {
  const [status, setStatus] = useState<BackgroundCanvasStatus | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [now, setNow] = useState(Date.now);
  const [lookup, setLookup] = useState(0);
  const retry = useCallback(() => setLookup((value) => value + 1), []);
  useEffect(() => {
    setStatus(null);
    setUnavailable(false);
    if (!conversationId) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const clock = setInterval(() => setNow(Date.now()), 1000);
    const poll = async () => {
      try {
        const next = await client.backgroundCanvasStatus(conversationId);
        if (disposed) return;
        setNow(Date.now());
        setStatus(next);
        setUnavailable(false);
      } catch (error) {
        if (!disposed) {
          const transient =
            error instanceof TypeError ||
            (error instanceof PlayerAIProblem &&
              (error.problem.status === 408 ||
                error.problem.status === 429 ||
                (error.problem.status >= 500 && error.problem.status < 600)));
          // Read the latest result, not the effect's initial status snapshot.
          setStatus((current) => {
            if (!transient || !current) return null;
            const stage = current.stage ?? current.state;
            const durable = [
              "needs_input",
              "no_change",
              "conflict",
              "failed",
              "expired",
              "awaiting_approval",
            ].includes(stage);
            const recentCompletion =
              ["updated", "rejected"].includes(stage) &&
              canvasStatusText(current, Date.now()) !== null;
            return durable || recentCompletion ? current : null;
          });
          setUnavailable(true);
        }
      } finally {
        if (!disposed) timer = setTimeout(poll, 2000);
      }
    };
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
      clearInterval(clock);
    };
  }, [client, conversationId, lookup]);
  const statusLabel = status ? canvasStatusText(status, now) : null;
  return {
    status,
    unavailable,
    retry,
    label: unavailable
      ? (statusLabel ?? "Canvas status unavailable")
      : statusLabel,
  };
}

export function useBackgroundCanvasStatus(
  client: Pick<PlayerAIClient, "backgroundCanvasStatus">,
  conversationId: string | null,
) {
  return useBackgroundCanvasWork(client, conversationId).label;
}
